import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Loader2, MessageCircle } from 'lucide-react';
import './Contact.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: '✅ Message sent successfully! We will get back to you within 24 hours.' 
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const data = await response.json();
        setSubmitStatus({ 
          type: 'error', 
          message: data.detail || 'Failed to send message. Please try again.' 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      info: '+91 93135 53726',
      link: 'tel:+919313553726'
    },
    {
      icon: Phone,
      title: 'Alternative',
      info: '+91 88663 23567',
      link: 'tel:+918866323567'
    },
    {
      icon: Mail,
      title: 'Email',
      info: 'Smilegnr@outlook.com',
      link: 'mailto:Smilegnr@outlook.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      info: 'Gandhinagar, Gujarat - 382016',
      link: '#'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      info: '+91 93135 53726',
      link: 'https://wa.me/919313553726'
    }
  ];

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <div className="contact-content">
          <div className="contact-info-section">
            <h3 className="contact-info-title">Contact Information</h3>
            <p className="contact-info-description">
              Reach out to us through any of the following channels. We're here to answer your questions.
            </p>

            <div className="contact-info-list">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a
                    key={index}
                    href={item.link}
                    className="contact-info-item"
                    target={item.link.startsWith('https') ? '_blank' : undefined}
                    rel={item.link.startsWith('https') ? 'noopener noreferrer' : undefined}
                    data-testid={`contact-${item.title.toLowerCase()}`}
                  >
                    <div className="contact-info-icon">
                      <Icon size={24} />
                    </div>
                    <div className="contact-info-text">
                      <div className="contact-info-label">{item.title}</div>
                      <div className="contact-info-value">{item.info}</div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          <div className="contact-form-section">
            {submitStatus.message && (
              <div className={`contact-alert ${submitStatus.type}`} data-testid="contact-alert">
                {submitStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span>{submitStatus.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="form-input"
                  placeholder="How can we help?"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  data-testid="contact-subject-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  className="form-textarea"
                  placeholder="Tell us more about your project..."
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  data-testid="contact-message-input"
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={loading}
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
